import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';
import { Plus, PackageCheck, Send, FileText, Trash2, Eye, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { isRecent } from '@/lib/uxHelpers';
import { useStats } from '@/contexts/StatsContext';
import { markListSeen, ENTITY_TYPES } from '@/lib/readState';
import { toastApiError } from '@/lib/errors';

function downloadIntakePdf(intakeId, protocolNumber, t) {
  const token = localStorage.getItem('ct-token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const url = `${baseUrl}/api/intakes/${intakeId}/protocol.pdf`;
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => {
      if (!r.ok) return r.json().then(d => { throw new Error(d.detail || t('intakes.error-pdf-unavailable')); });
      return r.blob();
    })
    .then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${protocolNumber || 'protokol'}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(err => toastApiError(err, t('intakes.error-pdf-failed')));
}

export default function IntakeRegistryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [newInstrument, setNewInstrument] = useState({ name: '', type: '', manufacturer: '', marking1: '', marking2: '' });
  const [detailIntake, setDetailIntake] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [filterText, setFilterText] = useState('');  // UX vrstva C
  const { refetch: refetchStats } = useStats();

  const load = () => {
    api.get('/intakes').then(r => setIntakes(r.data)).catch(() => {});
    api.get('/orders').then(r => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Badge = intakes s sentToCalibration=false. Po otevření listu mark-seen.
  useEffect(() => {
    if (intakes.length === 0) return;
    const notSentIds = intakes.filter(i => !i.sentToCalibration).map(i => i.id).filter(Boolean);
    if (notSentIds.length === 0) return;
    markListSeen(ENTITY_TYPES.INTAKE, notSentIds).then(() => refetchStats());
  }, [intakes, refetchStats]);

  // ADR-006 waterfall: dostupné jsou potvrzené i přijaté objednávky bez existujícího
  // intake (1:1). 'received' bereme taky — tlačítko „Přijmout" v Objednávkách stav
  // posune na 'received', ale příjem se pořád musí dát vytvořit (jinak slepá ulička).
  const ordersWithIntake = new Set(intakes.map(i => i.orderId).filter(Boolean));
  const availableOrders = orders.filter(o => ['confirmed', 'received'].includes(o.status) && !ordersWithIntake.has(o.id));

  const onOrderSelect = (orderId) => {
    setSelectedOrderId(orderId);
    if (!orderId) {
      setForm({ ...form, newInstruments: [] });
      return;
    }
    const order = orders.find(o => o.id === orderId);
    if (order && Array.isArray(order.instruments)) {
      // Pre-fill přístroji z objednávky — uživatel je může upravit (doplnit marking, atd.).
      // KRITICKÉ: zachovat `instrumentId` (FK na lab instruments), jinak send-to-calibration
      // následně selže fuzzy-matchem a nevytvoří per-sondu kalibrace.
      setForm({ ...form, newInstruments: order.instruments.map(i => ({
        instrumentId: i.instrumentId || '',
        name: i.name || '',
        type: i.type || '',
        manufacturer: i.manufacturer || '',
        marking1: i.marking1 || '',
        marking2: i.marking2 || '',
      })) });
    }
  };

  const selectIntake = (i) => { setSelected(i.id); setForm({ ...i }); };

  const removeIntake = async (i) => {
    if (!window.confirm(`Smazat příjem ${i.intakeProtocolNumber || ''}? Tuto akci nelze vrátit.`)) return;
    try {
      await api.delete(`/intakes/${i.id}`);
      if (selected === i.id) { setSelected(null); setForm({}); }
      load();
      toast.success('Příjem smazán');
    } catch (err) {
      toastApiError(err);
    }
  };

  const create = async (withProtocol) => {
    if (!selectedOrderId) {
      toast.error(t('intakes.error-pick-order'));
      return;
    }
    const instruments = form.newInstruments || [];
    if (instruments.length === 0) {
      toast.error(t('intakes.error-add-instrument'));
      return;
    }
    try {
      const res = await api.post('/intakes', {
        orderId: selectedOrderId,
        instruments,
        generateProtocol: withProtocol,
        notes: form.newNotes || '',
      });
      load();
      selectIntake(res.data);
      setForm({ ...form, newInstruments: [], newNotes: '' });
      setSelectedOrderId('');
      toast.success(withProtocol ? t('intakes.toast-accepted-with-pdf') : t('intakes.toast-accepted'));
      // Pokud BE auto-vytvořil sondy z portál_probes, dej vědět
      // a usnadni adminovi navigaci na doplnění metodiky.
      const sync = res.data?.portalProbeSync;
      if (sync && sync.totalCreated > 0) {
        toast.info(
          t('intakes.toast-portal-sync-info', { created: sync.totalCreated, instruments: sync.instruments.length }),
          { duration: 9000 }
        );
      }
      // Při „Přijmout a vygenerovat protokol" ihned stáhni PDF.
      if (withProtocol && res.data?.id) {
        downloadIntakePdf(res.data.id, res.data.intakeProtocolNumber, t);
      }
    } catch (err) {
      toastApiError(err);
    }
  };

  const sendToCalibration = async () => {
    if (!selected) return;
    // Pre-check (read-only náhled): kolik kalibrací vznikne a co se přeskočí.
    // Bez sond / metodiky přístroj kalibraci nevytvoří — proto blokujeme
    // odeslání, které by nevyrobilo nic, a u částečného skipu vyžádáme potvrzení.
    try {
      const pv = (await api.get(`/intakes/${selected}/calibration-preview`)).data;
      const skipped = [
        ...(pv.instrumentsWithoutProbes || []),
        ...(pv.unresolvedInstruments || []),
        ...(pv.instrumentsNeedingMethodology || []),
      ];
      if ((pv.willCreate || 0) === 0) {
        const reasons = [];
        if (pv.unresolvedInstruments?.length) reasons.push(t('intakes.reason-unresolved', { count: pv.unresolvedInstruments.length }));
        if (pv.instrumentsWithoutProbes?.length) reasons.push(t('intakes.reason-no-probes', { count: pv.instrumentsWithoutProbes.length }));
        if (pv.instrumentsNeedingMethodology?.length) reasons.push(t('intakes.reason-need-meth', { count: pv.instrumentsNeedingMethodology.length }));
        toast.error(t('intakes.preview-block', { reasons: reasons.join(', ') || t('intakes.toast-zero-default-reason') }), { duration: 9000 });
        return;
      }
      if (skipped.length > 0) {
        const ok = window.confirm(t('intakes.preview-confirm', { items: skipped.join(', '), count: pv.willCreate }));
        if (!ok) return;
      }
    } catch (err) {
      toastApiError(err);
      return;
    }
    try {
      const res = await api.put(`/intakes/${selected}/send-to-calibration`);
      load();
      refetchStats();
      const created = res.data?.calibrationsCreated || 0;
      const unresolved = res.data?.unresolvedInstruments || [];
      const noProbes = res.data?.instrumentsWithoutProbes || [];
      // Sondy z auto-syncu, kterým chybí metodika.
      const needMeth = res.data?.instrumentsNeedingMethodology || [];

      if (created === 0) {
        // Send-to-calibration prošel, ale nevyrobily se žádné kalibrace —
        // user by jinak v Kanbanu nic neviděl a netušil proč.
        const reasons = [];
        if (unresolved.length) reasons.push(t('intakes.reason-unresolved', { count: unresolved.length }));
        if (noProbes.length) reasons.push(t('intakes.reason-no-probes', { count: noProbes.length }));
        if (needMeth.length) {
          const totalProbes = needMeth.reduce((acc, i) => acc + (i.probeIds?.length || 0), 0);
          reasons.push(t('intakes.reason-need-meth', { count: totalProbes }));
        }
        toast.warning(
          t('intakes.toast-zero-calibrations', { reasons: reasons.join(', ') || t('intakes.toast-zero-default-reason') }),
          { duration: 9000 }
        );
        return;
      }

      const skippedItems = [];
      if (unresolved.length) skippedItems.push(t('intakes.skipped-unresolved', { count: unresolved.length }));
      if (noProbes.length) skippedItems.push(t('intakes.skipped-no-probes', { count: noProbes.length }));
      if (needMeth.length) {
        const totalProbes = needMeth.reduce((acc, i) => acc + (i.probeIds?.length || 0), 0);
        skippedItems.push(t('intakes.skipped-need-meth', { count: totalProbes }));
      }
      const skippedClause = skippedItems.length ? t('intakes.toast-sent-skipped-prefix', { items: skippedItems.join(', ') }) : '';
      const msg = t('intakes.toast-sent-success', { count: created, skipped: skippedClause });
      toast.success(msg, {
        action: {
          label: t('intakes.toast-sent-action-open'),
          onClick: () => window.location.assign('/calibrations'),
        },
      });
    } catch (err) { toastApiError(err); }
  };

  const addInstrumentToNew = () => {
    const instruments = [...(form.newInstruments || []), { ...newInstrument }];
    setForm({ ...form, newInstruments: instruments });
    setNewInstrument({ name: '', type: '', manufacturer: '', marking1: '', marking2: '' });
  };

  const removeInstrument = (idx) => {
    const instruments = (form.newInstruments || []).filter((_, i) => i !== idx);
    setForm({ ...form, newInstruments: instruments });
  };

  // UX vrstva C: search napříč fields.
  const matchesFilter = (i) => {
    if (!filterText.trim()) return true;
    const q = filterText.toLowerCase();
    const hay = [
      i.intakeProtocolNumber, i.orderNumber, i.customerName,
      ...(i.instruments || []).map(x => x.name),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  };
  const notSent = intakes.filter(i => !i.sentToCalibration && matchesFilter(i));
  const sent = intakes.filter(i => i.sentToCalibration && matchesFilter(i));

  const sel = useBulkSelect(notSent.map(i => i.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/intakes/${id}`));
    if (selected && ids.includes(selected)) { setSelected(null); setForm({}); }
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo`);
    else toast.success(`Smazáno ${ok}`);
  };

  return (
    <div data-testid="intake-registry-page" className="space-y-4 h-[calc(100vh-7rem)] flex flex-col">
      {/* Top: New intake form */}
      <div className="ct-panel">
        <div className="ct-panel-header flex items-center gap-2">
          <PackageCheck className="w-4 h-4" />
          {t('intakes.new-intake-title')}
        </div>
        <div className="p-4 space-y-3">
          {/* ADR-006 waterfall: vybrat potvrzenou objednávku */}
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('intakes.label-confirmed-order')}
            </label>
            <select
              data-testid="intake-order-select"
              value={selectedOrderId}
              onChange={e => onOrderSelect(e.target.value)}
              className="h-8 text-sm rounded px-2 flex-1 max-w-md"
              style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }}
            >
              <option value="">{t('intakes.select-order-placeholder')}</option>
              {availableOrders.map(o => (
                <option key={o.id} value={o.id}>
                  {o.orderNumber} · {o.customer?.company || '—'} · {t('intakes.list-instr-count', { count: (o.instruments || []).length })}
                </option>
              ))}
            </select>
            {availableOrders.length === 0 && (
              <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                {t('intakes.no-available-orders')}
              </span>
            )}
          </div>
          {/* Instrument table */}
          {(form.newInstruments || []).length > 0 && (
            <table className="w-full text-sm mb-2">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                  <th className="text-left py-1 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-name')}</th>
                  <th className="text-left py-1 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-type')}</th>
                  <th className="text-left py-1 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-manufacturer')}</th>
                  <th className="text-left py-1 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-marking-1')}</th>
                  <th className="text-left py-1 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-marking-2')}</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(form.newInstruments || []).map((inst, idx) => (
                  <tr key={idx} className="ct-table-row">
                    <td className="py-1 px-2">{inst.name}</td>
                    <td className="py-1 px-2 font-mono text-xs">{inst.type}</td>
                    <td className="py-1 px-2">{inst.manufacturer}</td>
                    <td className="py-1 px-2 font-mono text-xs">{inst.marking1}</td>
                    <td className="py-1 px-2 font-mono text-xs">{inst.marking2}</td>
                    <td className="py-1 px-2">
                      <button onClick={() => removeInstrument(idx)} style={{ color: 'var(--ct-error)' }}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="grid grid-cols-6 gap-2">
            <Input placeholder={t('intakes.instr-col-name')} value={newInstrument.name} onChange={e => setNewInstrument({ ...newInstrument, name: e.target.value })} className="h-8 text-sm" />
            <Input placeholder={t('intakes.instr-col-type')} value={newInstrument.type} onChange={e => setNewInstrument({ ...newInstrument, type: e.target.value })} className="h-8 text-sm" />
            <Input placeholder={t('intakes.instr-col-manufacturer')} value={newInstrument.manufacturer} onChange={e => setNewInstrument({ ...newInstrument, manufacturer: e.target.value })} className="h-8 text-sm" />
            <Input placeholder={t('intakes.instr-col-marking-1')} value={newInstrument.marking1} onChange={e => setNewInstrument({ ...newInstrument, marking1: e.target.value })} className="h-8 text-sm" />
            <Input placeholder={t('intakes.instr-col-marking-2')} value={newInstrument.marking2} onChange={e => setNewInstrument({ ...newInstrument, marking2: e.target.value })} className="h-8 text-sm" />
            <Button onClick={addInstrumentToNew} size="sm" className="ct-btn-primary h-8 gap-1">
              <Plus className="w-3 h-3" /> {t('intakes.btn-add-instrument')}
            </Button>
          </div>
          <div className="flex gap-2 pt-2 items-center">
            <Button
              data-testid="accept-without-protocol"
              onClick={() => create(false)}
              disabled={!selectedOrderId || !(form.newInstruments?.length)}
              className="ct-btn-secondary gap-1 text-xs"
              title={!selectedOrderId ? t('intakes.btn-accept-no-protocol-disabled-no-order') : (!(form.newInstruments?.length) ? t('intakes.btn-accept-no-protocol-disabled-no-instr') : undefined)}
            >
              <PackageCheck className="w-4 h-4" /> {t('intakes.btn-accept-no-protocol')}
            </Button>
            <Button
              data-testid="accept-with-protocol"
              onClick={() => create(true)}
              disabled={!selectedOrderId || !(form.newInstruments?.length)}
              className="ct-btn-primary gap-1 text-xs"
              title={!selectedOrderId ? t('intakes.btn-accept-no-protocol-disabled-no-order') : (!(form.newInstruments?.length) ? t('intakes.btn-accept-no-protocol-disabled-no-instr') : undefined)}
            >
              <FileText className="w-4 h-4" /> {t('intakes.btn-accept-with-protocol')}
            </Button>
            {!selectedOrderId && (
              <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                {t('intakes.hint-pick-order')}
              </span>
            )}
            {selectedOrderId && !(form.newInstruments?.length) && (
              <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                {t('intakes.hint-add-instruments')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Bottom: Completed intakes */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        <div className="ct-panel flex-1 flex flex-col">
          <div className="ct-panel-header">
            {t('intakes.list-not-sent-title', { count: notSent.length })}
          </div>
          <div className="px-2 py-2 space-y-2" style={{ borderBottom: '1px solid var(--ct-border)' }}>
            <Input
              placeholder={t('intakes.list-search-placeholder')}
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              className="h-8 text-xs"
            />
            <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="příjmů" />
          </div>
          <ScrollArea className="flex-1">
            {notSent.map(i => (
              <div
                key={i.id}
                className={`ct-table-row flex items-center gap-1 px-3 py-2 text-sm ${selected === i.id ? 'active' : ''} ${isRecent(i.receivedAt) ? 'ct-row-new' : ''}`}
              >
              <Checkbox
                data-testid={`intake-select-${i.id}`}
                checked={sel.isSelected(i.id)}
                onCheckedChange={() => sel.toggle(i.id)}
                className="flex-shrink-0"
              />
              <button
                onClick={() => selectIntake(i)}
                className="flex-1 min-w-0 text-left"
                title={isRecent(i.receivedAt) ? t('intakes.list-row-recent-title') : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{i.intakeProtocolNumber}</span>
                      <span className="text-xs">{t('intakes.list-instr-count', { count: (i.instruments || []).length })}</span>
                    </div>
                    {(i.orderNumber || i.customerName) && (
                      <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--ct-text-secondary)' }}>
                        {i.orderNumber && <span className="font-mono">{i.orderNumber}</span>}
                        {i.orderNumber && i.customerName && <span> · </span>}
                        {i.customerName && <span>{i.customerName}</span>}
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-mono ml-2 flex-shrink-0" style={{ color: 'var(--ct-text-secondary)' }}>
                    {i.receivedAt ? new Date(i.receivedAt).toLocaleDateString(t('common.date-locale')) : ''}
                  </span>
                </div>
              </button>
              <button
                onClick={() => removeIntake(i)}
                className="p-1 rounded flex-shrink-0"
                style={{ color: 'var(--ct-error, #dc2626)' }}
                title="Smazat příjem"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              </div>
            ))}
          </ScrollArea>
          <div className="p-3 border-t flex gap-2" style={{ borderColor: 'var(--ct-border)' }}>
            <Button
              data-testid="send-to-calibration-btn"
              onClick={sendToCalibration}
              disabled={!selected || intakes.find(i => i.id === selected)?.sentToCalibration}
              className="ct-btn-primary gap-1 text-xs"
            >
              <Send className="w-4 h-4" /> {t('intakes.btn-send-to-calibration')}
            </Button>
          </div>
        </div>

        {/* Protocols */}
        <div className="ct-panel w-72 flex-shrink-0 flex flex-col">
          <div className="ct-panel-header">{t('intakes.protocols-title')}</div>
          <ScrollArea className="flex-1">
            {intakes.filter(i => i.generateProtocol).map(i => (
              <button
                key={i.id}
                data-testid={`intake-protocol-${i.id}`}
                onClick={() => setDetailIntake(i)}
                className="ct-table-row w-full text-left px-3 py-2 text-sm flex items-center justify-between"
                style={{ borderBottom: '1px solid var(--ct-border)' }}
                title={t('intakes.protocol-row-title')}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{i.intakeProtocolNumber}</div>
                  <div className="text-xs truncate" style={{ color: 'var(--ct-text-secondary)' }}>
                    {i.receivedAt ? new Date(i.receivedAt).toLocaleDateString(t('common.date-locale')) : ''}
                    {' · '}
                    {t('intakes.list-instr-count', { count: (i.instruments || []).length })}
                  </div>
                  {(i.orderNumber || i.customerName) && (
                    <div className="text-xs truncate" style={{ color: 'var(--ct-text-disabled)' }}>
                      {i.orderNumber && <span className="font-mono">{i.orderNumber}</span>}
                      {i.orderNumber && i.customerName && <span> · </span>}
                      {i.customerName}
                    </div>
                  )}
                </div>
                <Eye className="w-3.5 h-3.5 flex-shrink-0 ml-2" style={{ color: 'var(--ct-text-secondary)' }} />
              </button>
            ))}
            {intakes.filter(i => i.generateProtocol).length === 0 && (
              <div className="p-4 text-center text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                {t('intakes.protocols-empty')}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Intake detail modal */}
      <Dialog open={!!detailIntake} onOpenChange={(v) => !v && setDetailIntake(null)}>
        <DialogContent
          data-testid="intake-detail-dialog"
          style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '700px' }}
        >
          <DialogHeader className="flex flex-row items-center justify-between pr-8">
            <DialogTitle style={{ color: 'var(--ct-accent)', fontFamily: 'Inter' }}>
              {t('intakes.dialog-title', { number: detailIntake?.intakeProtocolNumber })}
            </DialogTitle>
            {detailIntake && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => { const id = detailIntake.id; setDetailIntake(null); navigate(`/intakes/${id}`); }}
                  size="sm"
                  className="ct-btn-secondary gap-1 text-xs"
                  title={t('intakes.btn-open-detail-title')}
                >
                  <ExternalLink className="w-3.5 h-3.5" /> {t('intakes.btn-open-detail')}
                </Button>
                <Button
                  data-testid="download-intake-pdf-btn"
                  onClick={() => downloadIntakePdf(detailIntake.id, detailIntake.intakeProtocolNumber, t)}
                  size="sm"
                  className="ct-btn-secondary gap-1 text-xs"
                  style={{ color: 'var(--ct-accent)', borderColor: 'var(--ct-accent)' }}
                >
                  <Download className="w-3.5 h-3.5" /> {t('intakes.btn-download-pdf')}
                </Button>
              </div>
            )}
          </DialogHeader>
          {detailIntake && (
            <div className="space-y-4 text-sm">
              {(detailIntake.orderNumber || detailIntake.customerName) && (
                <div
                  className="p-3 rounded grid grid-cols-2 gap-3"
                  style={{ background: 'rgba(var(--ct-accent-rgb), 0.06)', border: '1px solid rgba(var(--ct-accent-rgb), 0.2)' }}
                >
                  <div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-order')}</div>
                    <div className="font-mono" style={{ color: 'var(--ct-accent)' }}>{detailIntake.orderNumber || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-customer')}</div>
                    <div>{detailIntake.customerName || '—'}</div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-received-by')}</div>
                  <div>{detailIntake.receivedByName || '—'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-received-at')}</div>
                  <div className="font-mono">{detailIntake.receivedAt ? new Date(detailIntake.receivedAt).toLocaleString(t('common.date-locale')) : '—'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-sent-to-calibration')}</div>
                  <div>
                    {detailIntake.sentToCalibration
                      ? <span style={{ color: 'var(--ct-success)' }}>{t('intakes.sent-yes-prefix')} {detailIntake.sentToCalibrationAt ? new Date(detailIntake.sentToCalibrationAt).toLocaleDateString(t('common.date-locale')) : ''}</span>
                      : <span style={{ color: 'var(--ct-warning)' }}>{t('intakes.sent-no')}</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-instr-count')}</div>
                  <div className="font-mono">{(detailIntake.instruments || []).length}</div>
                </div>
              </div>

              {(detailIntake.instruments || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-instruments')}</div>
                  <div className="rounded overflow-hidden" style={{ border: '1px solid var(--ct-border)' }}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: 'var(--ct-bg)', borderBottom: '1px solid var(--ct-border)' }}>
                          <th className="text-left py-1.5 px-2" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-name')}</th>
                          <th className="text-left py-1.5 px-2" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-type')}</th>
                          <th className="text-left py-1.5 px-2" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-manufacturer')}</th>
                          <th className="text-left py-1.5 px-2" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-marking-1')}</th>
                          <th className="text-left py-1.5 px-2" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.instr-col-marking-2')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailIntake.instruments || []).map((inst, idx) => (
                          <tr key={idx} className="ct-table-row">
                            <td className="py-1.5 px-2">{inst.name || '—'}</td>
                            <td className="py-1.5 px-2 font-mono">{inst.type || '—'}</td>
                            <td className="py-1.5 px-2">{inst.manufacturer || '—'}</td>
                            <td className="py-1.5 px-2 font-mono">{inst.marking1 || '—'}</td>
                            <td className="py-1.5 px-2 font-mono">{inst.marking2 || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {detailIntake.notes && (
                <div>
                  <div className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.label-notes')}</div>
                  <div className="p-2 rounded text-sm" style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
                    {detailIntake.notes}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
